import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ActivityIndicator, Platform, TextInput } from 'react-native';
import { GoogleSignin } from '@react-native-google-signin/google-signin';
import { signInWithGoogle, signInWithGooglePopup, signInWithEmailPassword, signOut } from '../../services/authService';
import CrossPlatformAlert from '../../components/CrossPlatformAlert';
import { useLanguage } from '../../contexts/LanguageContext';
import { addLoginEvent } from "../../services/auditService";

if (Platform.OS !== 'web') {
  GoogleSignin.configure({
    webClientId: process.env.EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID,
    hostedDomain: 'bieszczady.gopr.pl',
  });
}

export default function LoginScreen() {
  const [loading, setLoading] = useState(false);
  const [emailFormVisible, setEmailFormVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { t } = useLanguage();

  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      if (Platform.OS === 'web') {
        await handleWebGoogleLogin();
      } else {
        await handleMobileGoogleLogin();
      }
    } catch (error: any) {
      console.error('Google login error:', error);
      handleGoogleLoginError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleWebGoogleLogin = async () => {
    await signInWithGooglePopup();
    await addLoginEvent();
  };

  const handleMobileGoogleLogin = async () => {
    await GoogleSignin.hasPlayServices({ showPlayServicesUpdateDialog: true });
    const signInResult = await GoogleSignin.signIn();
    const idToken = signInResult.data?.idToken;
    if (!idToken) {
      throw new Error('No ID token received from Google Sign-In');
    }
    const user = await signInWithGoogle(idToken);
    await addLoginEvent();
    if (!user.email?.endsWith('@bieszczady.gopr.pl')) {
      await signOut();
      throw new Error(t('login.onlyGoprAllowed'));
    }
  };

  const handleGoogleLoginError = (error: any) => {
    let errorMessage = error.message || t('login.loginFailed');

    if (Platform.OS === 'web') {
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = t('login.cancelled');
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = t('login.popupBlocked');
      } else if (error.code === 'auth/unauthorized-domain') {
        errorMessage = t('login.unauthorizedDomain');
      }
    } else {
      if (error.code === 'SIGN_IN_CANCELLED' || error.message?.includes('cancelled')) {
        errorMessage = t('login.cancelled');
      } else if (error.message?.includes('@bieszczady.gopr.pl')) {
        errorMessage = t('login.onlyGoprAllowed');
      } else if (error.code === 'PLAY_SERVICES_NOT_AVAILABLE') {
        errorMessage = t('login.playServicesUnavailable');
      } else if (error.code === 'SIGN_IN_REQUIRED') {
        errorMessage = t('login.signInRequired');
      }
    }

    CrossPlatformAlert.alert(t('login.loginFailed'), errorMessage);
  };

  const handleEmailLogin = async () => {
    if (!email.trim()) {
      CrossPlatformAlert.alert(t('login.emailLoginFailed'), t('login.emailRequired'));
      return;
    }
    if (!password) {
      CrossPlatformAlert.alert(t('login.emailLoginFailed'), t('login.passwordRequired'));
      return;
    }
    setLoading(true);
    try {
      await signInWithEmailPassword(email.trim(), password);
      await addLoginEvent();
    } catch (error: any) {
      console.error('Email login error:', error);
      let errorMessage = error.message || t('login.emailLoginFailed');
      if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password') {
        errorMessage = t('login.invalidCredentials');
      } else if (error.code === 'auth/user-not-found') {
        errorMessage = t('login.userNotFound');
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = t('login.invalidCredentials');
      }
      CrossPlatformAlert.alert(t('login.emailLoginFailed'), errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.card}>
        <Text style={styles.title}>{t('login.title')}</Text>
        <Text style={styles.subtitle}>{t('login.subtitle')}</Text>
        {loading ? (
          <ActivityIndicator size="large" color="#2563EB" />
        ) : (
          <>
            <TouchableOpacity style={styles.button} onPress={handleGoogleLogin}>
              <Text style={styles.buttonText}>{t('login.signInWithGoogle')}</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.emailToggle}
              onPress={() => setEmailFormVisible(!emailFormVisible)}
            >
              <Text style={styles.emailToggleText}>
                {emailFormVisible ? t('login.hideEmailForm') : t('login.orSignInWithEmail')}
              </Text>
            </TouchableOpacity>

            {emailFormVisible && (
              <View style={styles.emailForm}>
                <TextInput
                  style={styles.input}
                  placeholder={t('login.emailPlaceholder')}
                  placeholderTextColor="#94A3B8"
                  value={email}
                  onChangeText={setEmail}
                  autoCapitalize="none"
                  keyboardType="email-address"
                  textContentType="emailAddress"
                  autoComplete="email"
                />
                <TextInput
                  style={styles.input}
                  placeholder={t('login.passwordPlaceholder')}
                  placeholderTextColor="#94A3B8"
                  value={password}
                  onChangeText={setPassword}
                  secureTextEntry
                  textContentType="password"
                  autoComplete="password"
                />
                <TouchableOpacity style={styles.emailButton} onPress={handleEmailLogin}>
                  <Text style={styles.buttonText}>{t('login.signInWithEmail')}</Text>
                </TouchableOpacity>
              </View>
            )}
          </>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
    padding: 24,
  },
  card: {
    width: '100%',
    maxWidth: 440,
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 40,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.08,
    shadowRadius: 16,
    elevation: 4,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  title: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#1E293B',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    color: '#64748B',
    marginBottom: 40,
    textAlign: 'center',
  },
  button: {
    backgroundColor: '#2563EB',
    paddingHorizontal: 32,
    paddingVertical: 14,
    borderRadius: 8,
    width: '100%',
    alignItems: 'center',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  emailToggle: {
    marginTop: 20,
    paddingVertical: 8,
  },
  emailToggleText: {
    color: '#2563EB',
    fontSize: 14,
  },
  emailForm: {
    width: '100%',
    marginTop: 8,
    gap: 12,
  },
  input: {
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    color: '#1E293B',
    backgroundColor: '#FFFFFF',
  },
  emailButton: {
    backgroundColor: '#475569',
    paddingHorizontal: 32,
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
});
